import API from '../../utils/constant';
import { notifyAiLimitFromError } from '../components/ai/aiLimitEvents';
import type {
  AiCheckoutResponse, AiVerifyResponse, AiWallet, AiWalletBlock,
  AcademicResultRow, AlertInstance, CaseEvent, CommitmentsResponse, InterventionCase, CaseDetailResponse, StudentCasesResponse, StudentNote, StudentOverview, StudentsListResponse, TeacherAssignment, WellbeingResponse, AnalyticsGradesResponse, AnalyticsAcademicResponse, AnalyticsEngagementResponse, AnalyticsWellbeingResponse, AnalyticsInterventionsResponse, StudentEngagementResponse, StudentInterestsResponse, AlertRuleSettingsRow, AlertRulesListResponse, StudentViewersResponse, SchoolTasksPage, StaffSchoolTask, CreateNoticeBody, TaskTracker, TaskNudgeResult, Section, SectionsListResponse, SectionRosterResponse, BulkOpResponse, ClassPerfOverviewResponse, ClassPerfBySubjectResponse, ClassPerfExamsResponse, ClassPerfTrendsResponse, ClassPerfLeaderboardResponse, ClassPerfChapterMasteryResponse, ClassPerfQuestionsResponse, ClassPerfSyllabusResponse, SyllabusEntriesListResponse, SyllabusEntry, StudentWeeklyTrendResponse, StudentChapterMasteryResponse, StudentMissedQuestionsResponse, AutoGroupsResponse, StudentGroup, StudentGroupsListResponse, StudentGroupWriteBody, StudentGroupRefreshResponse, GroupingLens, GroupingSuggestionsResponse, AssignableGroupsResponse, SectionAlertResolution, SectionAlertOverridesListResponse, CoachScope, CoachThread, CoachThreadsResponse, CoachMessagesResponse, CoachSendResponse, CoachMessage, CoachStartersResponse, CoachAuditResponse, CoachStreamEvent, CoachReport, CoachLanguage, InstitutionSettingsResponse, ParentDigestListResponse, ParentDigestRow, StudentParentResponse, Guardian, GuardianInput, ExamReportResponse, ResultsReleaseState, ResultComment, CyclesListResponse, CycleCreatePayload, CycleCreateResponse, AssessmentCycle, CycleGain, CycleStatus, StudentCycleRow, AcademicDashboardResponse, RollNumbersResponse, RollNumbersAssignResponse, PaperDraft, PaperDraftsListResponse, PaperDraftCreatePayload, PaperDraftPatchPayload, PaperDraftSaveResponse, ExamDelivery, ExamDeliveryResponse, SheetsListResponse, SheetsUploadResponse, SheetDetail, SheetPatchPayload, SheetsConfirmMatchedResponse, PaperExamsListResponse, ApproveJoinRequestBody, ApproveJoinRequestResponse, AnnotateBook, AnnotateResult, Attachment, AuthoringChapter, AuthoringExam, AuthoringQuestion, ExamBankDetail, ExamBankListResponse, ExamBankParams, MeTeaching, ImportedQuestion, QuestionPayload, MarkingSummary, ExamMarking, MarkingQuestion, MarkingStatus, ConfirmMarkResult, PaperWritten, StudentExamBreakdown, StudentNationalReadiness, StudentPatterns,
} from './types';

/**
 * Save a fetched blob through a temporary object URL. Every API call needs the
 * Authorization header, so PDFs are fetched through the axios instance and
 * handed to the browser this way instead of via a plain link.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** The URL of an exam's printable answer sheet (fetch it with downloadAnswerSheet). */
export function answerSheetPdfUrl(examId: number): string {
  return `/api/school/exams/${examId}/answer-sheet.pdf`;
}

export const SchoolApi = {
  // ----- staff "My AI" wallet -----
  aiWallet: () => API.get<AiWallet>('/api/school/ai/wallet/').then(r => r.data),
  aiWalletUpdate: (body: { use_own_key_first: boolean }) =>
    API.patch<AiWalletBlock>('/api/school/ai/wallet/', body).then(r => r.data),
  aiKeySave: (key: string) => API.put<AiWalletBlock>('/api/school/ai/wallet/key/', { key }).then(r => r.data),
  aiKeyRemove: () => API.delete<AiWalletBlock>('/api/school/ai/wallet/key/').then(r => r.data),
  aiCheckout: (body: { bundle_id: string; return_url: string }) =>
    API.post<AiCheckoutResponse>('/api/school/ai/wallet/checkout/', body).then(r => r.data),
  aiVerify: (tx_ref: string) =>
    API.post<AiVerifyResponse>('/api/school/ai/wallet/verify/', { tx_ref }).then(r => r.data),

  /** Student 360: one exam attempt against the class that sat it. */
  studentExamBreakdown: (id: number, resultId: number) =>
    API.get<StudentExamBreakdown>(`/api/school/students/${id}/exams/${resultId}/`).then(r => r.data),
  /** Student 360: patterns across the student's exams (optionally one subject). */
  studentPatterns: (id: number, subject: number | 'all' = 'all') =>
    API.get<StudentPatterns>(`/api/school/students/${id}/patterns/`, { params: subject === 'all' ? {} : { subject } }).then(r => r.data),
  studentNationalReadiness: (id: number) =>
    API.get<StudentNationalReadiness>(`/api/school/students/${id}/national-readiness/`).then(r => r.data),

  /** The answer sheet an exam will print, as a PNG drawn by the same renderer as the PDF. */
  paperSheetPreview: (p: { name: string; grade: string; bubbled: number[]; teacher: number[] }, signal?: AbortSignal) =>
    API.get<Blob>('/api/school/paper-exams/sheet-preview.png', {
      params: { name: p.name, grade: p.grade, bubbled: p.bubbled.join(','), teacher: p.teacher.join(',') },
      responseType: 'blob', signal,
    }).then(r => r.data),

  studentsList: (params: { grade?: number; section?: number | 'none' } = {}) =>
    API.get<StudentsListResponse>('/api/school/students/', { params }).then(r => r.data),

  studentOverview: (id: number) =>
    API.get<StudentOverview>(`/api/school/students/${id}/overview/`).then(r => r.data),

  studentAcademic: (id: number) =>
    API.get<{ results: AcademicResultRow[] }>(`/api/school/students/${id}/academic/`).then(r => r.data),

  studentCommitments: (id: number) =>
    API.get<CommitmentsResponse>(`/api/school/students/${id}/commitments/`).then(r => r.data),

  studentWellbeing: (id: number) =>
    API.get<WellbeingResponse>(`/api/school/students/${id}/wellbeing/`).then(r => r.data),

  studentCases: (id: number) =>
    API.get<StudentCasesResponse>(`/api/school/students/${id}/cases/`).then(r => r.data),

  studentEngagement: (id: number) =>
    API.get<StudentEngagementResponse>(`/api/school/students/${id}/engagement/`).then(r => r.data),

  studentInterests: (id: number) =>
    API.get<StudentInterestsResponse>(`/api/school/students/${id}/interests/`).then(r => r.data),

  studentViewers: (studentId: number) =>
    API.get<StudentViewersResponse>(`/api/school/students/${studentId}/viewers/`).then(r => r.data),

  studentWeeklyTrend: (id: number, params: { subject?: number; weeks?: number; scope?: string }) =>
    API.get<StudentWeeklyTrendResponse>(
      `/api/school/students/${id}/weekly-trend/`, { params }).then(r => r.data),

  studentChapterMastery: (id: number, params: { subject?: number; weeks?: number }) =>
    API.get<StudentChapterMasteryResponse>(
      `/api/school/students/${id}/chapter-mastery/`, { params }).then(r => r.data),

  studentMissedQuestions: (id: number, params: { subject?: number; weeks?: number; limit?: number; min_attempts?: number }) =>
    API.get<StudentMissedQuestionsResponse>(
      `/api/school/students/${id}/missed-questions/`, { params }).then(r => r.data),

  alertsInbox: (params: { severity?: string; rule?: string; status?: string; grade?: number; section?: number | 'none' } = {}) =>
    API.get<{ results: AlertInstance[] }>('/api/school/alerts/inbox/', { params }).then(r => r.data),

  alertDetail: (alertId: number) =>
    API.get<AlertInstance>(`/api/school/alerts/${alertId}/`).then(r => r.data),

  alertAction: (alertId: number, action: 'acknowledge' | 'snooze' | 'dismiss' | 'resolve', body: Record<string, unknown> = {}) =>
    API.post<AlertInstance>(`/api/school/alerts/${alertId}/${action}/`, body).then(r => r.data),

  casesList: () =>
    API.get<InterventionCase[]>('/api/school/cases/').then(r => r.data),

  caseDetail: (id: number) =>
    API.get<CaseDetailResponse>(`/api/school/cases/${id}/`).then(r => r.data),

  caseCreate: (body: { student: number; category: string; alert_id?: number }) =>
    API.post<InterventionCase>('/api/school/cases/', body).then(r => r.data),

  caseTransition: (id: number, body: { to: string; outcome?: string; outcome_note?: string }) =>
    API.post<InterventionCase>(`/api/school/cases/${id}/transition/`, body).then(r => r.data),

  caseEventCreate: (caseId: number, body: { kind: string; body?: string; payload?: Record<string, unknown> }) =>
    API.post<CaseEvent>(`/api/school/cases/${caseId}/events/`, body).then(r => r.data),

  caseNudge: (caseId: number, body: { title: string; body: string }) =>
    API.post<{ event_id: number; sent: boolean }>(`/api/school/cases/${caseId}/nudge/`, body).then(r => r.data),

  notesList: (studentId?: number) =>
    API.get<StudentNote[]>('/api/school/notes/', { params: studentId ? { student: studentId } : {} }).then(r => r.data),

  noteCreate: (body: { student: number; body: string; visibility: string; pinned?: boolean }) =>
    API.post<StudentNote>('/api/school/notes/', body).then(r => r.data),

  noteDelete: (id: number) =>
    API.delete<void>(`/api/school/notes/${id}/`).then(() => undefined),

  teacherAssignmentsList: () =>
    API.get<TeacherAssignment[]>('/api/school/teacher-assignments/').then(r => r.data),

  teacherAssignmentCreate: (body: {
    teacher: number; school_grade: number; subject: number | null; is_homeroom: boolean;
    /** null = the whole grade; required for a homeroom teacher. */
    section?: number | null;
  }) => API.post<TeacherAssignment>('/api/school/teacher-assignments/', body).then(r => r.data),

  teacherAssignmentDelete: (id: number) =>
    API.delete<void>(`/api/school/teacher-assignments/${id}/`).then(() => undefined),

  // Analytics (spec §6)
  dashboardAcademic: (params: { weeks?: number } = {}) =>
    API.get<AcademicDashboardResponse>('/api/school/dashboard/academic/', { params }).then(r => r.data),

  approveJoinRequest: (id: number, body: ApproveJoinRequestBody) =>
    API.post<ApproveJoinRequestResponse>(`/api/school/requests/${id}/approve/`, body).then(r => r.data),

  schoolTasks: (params: { kind?: string; state?: string; grade?: number; page: number }) =>
    API.get<SchoolTasksPage>('/api/school/tasks/', { params }).then(r => r.data),
  createNotice: (body: CreateNoticeBody) =>
    API.post<StaffSchoolTask>('/api/school/tasks/notices/', body).then(r => r.data),
  taskTracker: (id: number) =>
    API.get<TaskTracker>(`/api/school/tasks/${id}/tracker/`).then(r => r.data),
  nudgeTask: (id: number) =>
    API.post<TaskNudgeResult>(`/api/school/tasks/${id}/nudge/`).then(r => r.data),
  archiveTask: (id: number) =>
    API.delete<{ id: number; state: string }>(`/api/school/tasks/${id}/`).then(r => r.data),

  analyticsGrades: () =>
    API.get<AnalyticsGradesResponse>('/api/school/analytics/grades/').then(r => r.data),

  analyticsAcademic: (params: { grade: number; subject?: number | 'all'; weeks?: number; section?: number }) =>
    API.get<AnalyticsAcademicResponse>('/api/school/analytics/academic/', { params }).then(r => r.data),

  analyticsEngagement: (params: { grade: number; section?: number }) =>
    API.get<AnalyticsEngagementResponse>('/api/school/analytics/engagement/', { params }).then(r => r.data),

  analyticsWellbeing: (params: { grade: number; section?: number }) =>
    API.get<AnalyticsWellbeingResponse>('/api/school/analytics/wellbeing/', { params }).then(r => r.data),

  analyticsInterventions: (params: { grade: number; section?: number }) =>
    API.get<AnalyticsInterventionsResponse>('/api/school/analytics/interventions/', { params }).then(r => r.data),

  // Phase 2-F Settings -> Alerts
  alertRulesList: () =>
    API.get<AlertRulesListResponse>('/api/school/alert-rules/').then(r => r.data),

  alertRuleOverride: (code: string, body: {
    is_enabled: boolean;
    threshold_overrides: Record<string, unknown>;
  }) =>
    API.put<AlertRuleSettingsRow>(`/api/school/alert-rules/${code}/override/`, body).then(r => r.data),

  // Phase 2.5-A Sections (spec §6)
  sectionsList: (params: { grade: number }) =>
    API.get<SectionsListResponse>('/api/school/sections/', { params }).then(r => r.data),

  sectionCreate: (body: { school_grade: number; name: string }) =>
    API.post<Section>('/api/school/sections/', body).then(r => r.data),

  sectionDetail: (id: number) =>
    API.get<Section>(`/api/school/sections/${id}/`).then(r => r.data),

  sectionUpdate: (id: number, body: { name: string }) =>
    API.patch<Section>(`/api/school/sections/${id}/`, body).then(r => r.data),

  sectionDelete: (id: number) =>
    API.delete(`/api/school/sections/${id}/`).then(r => r.data),

  sectionRoster: (id: number) =>
    API.get<SectionRosterResponse>(`/api/school/sections/${id}/students/`).then(r => r.data),

  sectionAddStudents: (id: number, body: { student_ids: number[] }) =>
    API.post<BulkOpResponse>(`/api/school/sections/${id}/students/`, body).then(r => r.data),

  sectionRemoveStudents: (id: number, body: { student_ids: number[] }) =>
    API.delete<BulkOpResponse>(`/api/school/sections/${id}/students/`,
      { data: body }).then(r => r.data),

  bulkAssignSection: (body: { student_ids: number[]; section_id: number | null }) =>
    API.post<BulkOpResponse>('/api/school/students/bulk-assign-section/', body).then(r => r.data),

  teachersInInstitution: () =>
    API.get<{ teachers: Array<{ profile_id: number; user_id: number; username: string; first_name: string; last_name: string }> }>(
      '/api/school/teachers/'
    ).then(r => r.data),

  setSectionHomeroom: (body: { section_id: number; teacher_profile_id: number | null }) =>
    API.post<{ ok: true }>('/api/school/teacher-assignments/section-homeroom/', body).then(r => r.data),

  // Phase 3A §5.5.3, §10.1 — designated counselor
  counselorsInInstitution: () =>
    API.get<{ teachers: Array<{ profile_id: number; user_id: number; username: string; first_name: string; last_name: string }> }>(
      '/api/school/teachers/', { params: { role: 'counselor' } }
    ).then(r => r.data),

  institutionSettings: () =>
    API.get<InstitutionSettingsResponse>('/api/school/institution/settings/').then(r => r.data),

  setDesignatedCounselor: (profile_id: number | null) =>
    API.put<InstitutionSettingsResponse>('/api/school/institution/settings/',
      { designated_counselor_profile_id: profile_id }).then(r => r.data),

  classPerfOverview: (params: { grade: number; section?: number | 'all'; subject?: number | 'all'; weeks?: number }) =>
    API.get<ClassPerfOverviewResponse>('/api/school/class-performance/overview/', { params }).then(r => r.data),

  classPerfBySubject: (params: { grade: number; section?: number | 'all' }) =>
    API.get<ClassPerfBySubjectResponse>('/api/school/class-performance/by-subject/', { params }).then(r => r.data),

  classPerfExams: (params: { grade: number; section?: number | 'all'; subject?: number | 'all'; scope?: string; weeks?: number }) =>
    API.get<ClassPerfExamsResponse>('/api/school/class-performance/exams/', { params }).then(r => r.data),

  classPerfTrends: (params: { grade: number; section?: number | 'all'; subject?: number | 'all'; weeks?: number }) =>
    API.get<ClassPerfTrendsResponse>('/api/school/class-performance/trends/', { params }).then(r => r.data),

  classPerfLeaderboard: (params: { grade: number; subject: number | 'all'; weeks?: number }) =>
    API.get<ClassPerfLeaderboardResponse>('/api/school/class-performance/leaderboard/', { params }).then(r => r.data),

  classPerfChapterMastery: (params: { grade: number; subject: number; section?: number | 'all'; weeks?: number }) =>
    API.get<ClassPerfChapterMasteryResponse>('/api/school/class-performance/chapter-mastery/', { params }).then(r => r.data),

  classPerfQuestions: (params: { grade: number; section?: number | 'all'; subject?: number | 'all'; weeks?: number; limit?: number; min_attempts?: number }) =>
    API.get<ClassPerfQuestionsResponse>('/api/school/class-performance/questions/', { params }).then(r => r.data),

  classPerfSyllabus: (params: { grade: number; section: number; weeks?: number }) =>
    API.get<ClassPerfSyllabusResponse>('/api/school/class-performance/syllabus/', { params }).then(r => r.data),

  syllabusEntriesList: (params: { section: number; week_start?: string; subject?: number }) =>
    API.get<SyllabusEntriesListResponse>('/api/school/syllabus-entries/', { params }).then(r => r.data),

  syllabusEntryCreate: (body: { section: number; subject: number; chapter: number; week_start: string; notes?: string }) =>
    API.post<SyllabusEntry>('/api/school/syllabus-entries/', body).then(r => r.data),

  syllabusEntryUpdate: (id: number, body: { notes?: string }) =>
    API.patch<SyllabusEntry>(`/api/school/syllabus-entries/${id}/`, body).then(r => r.data),

  syllabusEntryDelete: (id: number) =>
    API.delete(`/api/school/syllabus-entries/${id}/`).then(r => r.data),

  // Phase 2.5-E grouping + section alert overrides
  sectionAutoGroups: (sectionId: number, params: { weeks?: number; threshold?: number }) =>
    API.get<AutoGroupsResponse>(
      `/api/school/sections/${sectionId}/auto-groups/`, { params }).then(r => r.data),

  studentGroupsList: (sectionId: number, subject?: number | null) =>
    API.get<StudentGroupsListResponse>(
      `/api/school/sections/${sectionId}/groups/`, subject != null ? { params: { subject } } : undefined).then(r => r.data),

  studentGroupCreate: (sectionId: number, body: StudentGroupWriteBody) =>
    API.post<StudentGroup>(
      `/api/school/sections/${sectionId}/groups/`, body).then(r => r.data),

  studentGroupUpdate: (sectionId: number, groupId: number,
                       body: Partial<StudentGroupWriteBody>) =>
    API.patch<StudentGroup>(
      `/api/school/sections/${sectionId}/groups/${groupId}/`, body).then(r => r.data),

  studentGroupDelete: (sectionId: number, groupId: number) =>
    API.delete(`/api/school/sections/${sectionId}/groups/${groupId}/`).then(() => undefined),

  /** Re-run a saved group's lens/band with its saved criteria and replace its members. */
  studentGroupRefresh: (sectionId: number, groupId: number) =>
    API.post<StudentGroupRefreshResponse>(
      `/api/school/sections/${sectionId}/groups/${groupId}/refresh/`).then(r => r.data),

  /** Teacher grouping: suggested groups for one section + subject under a lens. */
  groupingSuggestions: (params: { grade: number; section: number; subject: number; lens: GroupingLens; chapter?: number; weeks?: number }) =>
    API.get<GroupingSuggestionsResponse>('/api/school/class-performance/grouping/', { params }).then(r => r.data),

  /** Saved groups the viewer may give work of this grade + subject to. */
  assignableGroups: (params: { grade: number; subject: number }) =>
    API.get<AssignableGroupsResponse>('/api/school/student-groups/', { params }).then(r => r.data),

  sectionAlertOverridesList: (sectionId: number) =>
    API.get<SectionAlertOverridesListResponse>(
      `/api/school/sections/${sectionId}/alert-overrides/`).then(r => r.data),

  sectionAlertOverrideUpsert: (sectionId: number, ruleCode: string,
                                body: { is_enabled: boolean | null; threshold_config_override: Record<string, unknown> }) =>
    API.put<SectionAlertResolution>(
      `/api/school/sections/${sectionId}/alert-overrides/${ruleCode}/`, body).then(r => r.data),

  sectionAlertOverrideDelete: (sectionId: number, ruleCode: string) =>
    API.delete(`/api/school/sections/${sectionId}/alert-overrides/${ruleCode}/`).then(() => undefined),

  coachThreads: (scope: CoachScope) =>
    API.get<CoachThreadsResponse>('/api/school/coach/threads/',
      { params: { scope_type: scope.type, ...(scope.id ? { scope_id: scope.id } : {}) } }).then(r => r.data),

  coachCreateThread: (scope: CoachScope) =>
    API.post<CoachThread>('/api/school/coach/threads/', {
      scope_type: scope.type, scope_id: scope.id ?? null,
    }).then(r => r.data),

  coachMessages: (threadId: number, before?: number) =>
    API.get<CoachMessagesResponse>(
      `/api/school/coach/threads/${threadId}/messages/`,
      { params: before ? { before, limit: 30 } : { limit: 30 } }).then(r => r.data),

  coachSend: (threadId: number, content: string) =>
    API.post<CoachSendResponse>(
      `/api/school/coach/threads/${threadId}/messages/`,
      { content }).then(r => r.data),

  coachPatchThread: (threadId: number, body: { title?: string; archived?: boolean }) =>
    API.patch<CoachThread>(
      `/api/school/coach/threads/${threadId}/`, body).then(r => r.data),

  coachDeleteThread: (threadId: number) =>
    API.delete(`/api/school/coach/threads/${threadId}/`).then(() => undefined),

  coachFeedback: (threadId: number, messageId: number, body: { value: -1 | 0 | 1; note?: string }) =>
    API.post<CoachMessage>(`/api/school/coach/threads/${threadId}/messages/${messageId}/feedback/`, body).then(r => r.data),

  coachStarters: (scope: CoachScope) =>
    API.get<CoachStartersResponse>('/api/school/coach/starters/',
      { params: { scope_type: scope.type, ...(scope.id ? { scope_id: scope.id } : {}) } }).then(r => r.data),

  coachAudit: (params: { page?: number; user?: number; scope_type?: string; scope_id?: number } = {}) =>
    API.get<CoachAuditResponse>('/api/school/coach/audit/', { params }).then(r => r.data),

  // Phase 3B — Parent digest (spec §6.2.10, §7.2 Tab 8)
  parentDigestsList: (params: { week?: string } = {}) =>
    API.get<ParentDigestListResponse>('/api/school/parent-digests/', { params }).then(r => r.data),

  parentDigestResend: (id: number) =>
    API.post<ParentDigestRow>(`/api/school/parent-digests/${id}/resend/`).then(r => r.data),

  studentParent: (id: number) =>
    API.get<StudentParentResponse>(`/api/school/students/${id}/parent/`).then(r => r.data),

  guardianCreate: (studentId: number, body: GuardianInput) =>
    API.post<Guardian>(`/api/school/students/${studentId}/guardians/`, body).then(r => r.data),

  guardianUpdate: (studentId: number, id: number, body: GuardianInput) =>
    API.patch<Guardian>(`/api/school/students/${studentId}/guardians/${id}/`, body).then(r => r.data),

  guardianDelete: (studentId: number, id: number) =>
    API.delete(`/api/school/students/${studentId}/guardians/${id}/`).then(() => undefined),

  /** AI-written letter for the weekly parent report; `facts` is what the panel derived for the PDF. */
  studentParentLetter: (id: number, facts: Record<string, unknown>) =>
    API.post<{ letter: string; source: 'ai' }>(`/api/school/students/${id}/parent-letter/`, { facts }).then(r => r.data),

  examReport: (examId: number, params: { section?: number | 'all' } = {}) =>
    API.get<ExamReportResponse>(`/api/school/exams/${examId}/report/`, {
      params: params.section === 'all' ? {} : params,
    }).then(r => r.data),

  releaseResults: (examId: number) =>
    API.post<ResultsReleaseState>(`/api/school/exams/${examId}/results/release/`).then(r => r.data),
  unreleaseResults: (examId: number) =>
    API.post<ResultsReleaseState>(`/api/school/exams/${examId}/results/unrelease/`).then(r => r.data),
  saveResultComment: (examId: number, studentId: number, body: string) =>
    API.put<ResultComment>(`/api/school/exams/${examId}/results/${studentId}/comment/`, { body }).then(r => r.data),
  deleteResultComment: (examId: number, studentId: number) =>
    API.delete(`/api/school/exams/${examId}/results/${studentId}/comment/`).then(() => undefined),

  cyclesList: (params: { grade?: number; section?: number; subject?: number; status?: CycleStatus } = {}) =>
    API.get<CyclesListResponse>('/api/school/assessment-cycles/', { params }).then(r => r.data),

  cycleCreate: (payload: CycleCreatePayload) =>
    API.post<CycleCreateResponse>('/api/school/assessment-cycles/', payload).then(r => r.data),

  cycleDetail: (id: number) =>
    API.get<AssessmentCycle>(`/api/school/assessment-cycles/${id}/`).then(r => r.data),

  cycleGain: (id: number, live = false) =>
    API.get<CycleGain>(`/api/school/assessment-cycles/${id}/gain/`, { params: live ? { live: 1 } : {} }).then(r => r.data),

  cycleClose: (id: number) =>
    API.post<AssessmentCycle>(`/api/school/assessment-cycles/${id}/close/`).then(r => r.data),

  studentCycles: (studentId: number) =>
    API.get<{ cycles: StudentCycleRow[] }>(`/api/school/students/${studentId}/cycles/`).then(r => r.data),

  /** A coach turn as a stream of typed events (a question, a report, or regenerating the last answer). */
  coachStream: async (
    path: { threadId: number; regenerateMessageId?: number },
    body: { content?: string; report?: CoachReport; language?: CoachLanguage },
    onEvent: (ev: CoachStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const url = path.regenerateMessageId
      ? `/api/school/coach/threads/${path.threadId}/messages/${path.regenerateMessageId}/regenerate/`
      : `/api/school/coach/threads/${path.threadId}/messages/stream/`;
    // Mirror constant.tsx's readToken: prefer localStorage if remember=1, else sessionStorage.
    const rememberOn = ((): boolean => {
      try { return localStorage.getItem('auth_remember') === '1'; } catch { return true; }
    })();
    const token = ((): string | null => {
      try {
        const ls = localStorage.getItem('token');
        const ss = sessionStorage.getItem('token');
        return rememberOn ? (ls || ss) : (ss || ls);
      } catch { return null; }
    })();
    const res = await fetch(url, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let data: { detail?: string } | undefined;
      try { data = await res.json(); } catch { /* not JSON */ }
      const err = new Error(data?.detail || `coach stream HTTP ${res.status}`) as Error & { status?: number; data?: unknown };
      err.status = res.status;
      err.data = data;
      // A blocked AI call opens the global "AI credit ended" dialog.
      if (res.status === 429) notifyAiLimitFromError(err);
      throw err;
    }
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const raw = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        if (!raw.startsWith('data: ')) continue;
        const payload = raw.slice(6).trim();
        if (payload === '[DONE]') return;
        try {
          const ev = JSON.parse(payload) as CoachStreamEvent;
          if (ev && typeof ev === 'object' && 'type' in ev) onEvent(ev);
        } catch { /* ignore a malformed line */ }
      }
    }
  },

  // ----- paper exams (spec §5) -----
  paperExamsList: () =>
    API.get<PaperExamsListResponse>('/api/school/paper-exams/').then(r => r.data),

  rollNumbers: (gradeId: number) =>
    API.get<RollNumbersResponse>(`/api/school/grades/${gradeId}/roll-numbers/`).then(r => r.data),

  rollNumbersAssign: (gradeId: number) =>
    API.post<RollNumbersAssignResponse>(`/api/school/grades/${gradeId}/roll-numbers/assign/`, {}).then(r => r.data),

  downloadRollRoster: (gradeId: number, filename: string) =>
    API.get<Blob>(`/api/school/grades/${gradeId}/roll-numbers/`, { params: { format: 'pdf' }, responseType: 'blob' })
      .then(r => saveBlob(r.data, filename)),

  paperDraftsList: (params: { status?: string; limit?: number } = {}) =>
    API.get<PaperDraftsListResponse>('/api/school/paper-exams/drafts/', { params }).then(r => r.data),

  paperDraftsCreate: (payload: PaperDraftCreatePayload) => {
    const form = new FormData();
    form.append('grade', String(payload.grade));
    form.append('subject', String(payload.subject));
    form.append('name', payload.name);
    for (const f of payload.files) form.append('files', f);
    return API.post<PaperDraft>('/api/school/paper-exams/drafts/', form,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  paperDraftsDetail: (id: number) =>
    API.get<PaperDraft>(`/api/school/paper-exams/drafts/${id}/`).then(r => r.data),

  paperDraftsPatch: (id: number, body: PaperDraftPatchPayload) =>
    API.patch<PaperDraft>(`/api/school/paper-exams/drafts/${id}/`, body).then(r => r.data),

  paperDraftsSave: (id: number) =>
    API.post<PaperDraftSaveResponse>(`/api/school/paper-exams/drafts/${id}/save/`, {}).then(r => r.data),

  examDelivery: (examId: number, delivery: ExamDelivery) =>
    API.post<ExamDeliveryResponse>(`/api/school/exams/${examId}/delivery/`, { delivery }).then(r => r.data),

  downloadAnswerSheet: (examId: number, filename: string) =>
    API.get<Blob>(answerSheetPdfUrl(examId), { responseType: 'blob' })
      .then(r => saveBlob(r.data, filename)),

  uploadTaskAttachments: (taskId: number, files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return API.post<{ attachments: Attachment[] }>(`/api/school/tasks/${taskId}/attachments/`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  deleteTaskAttachment: (taskId: number, id: number) =>
    API.delete(`/api/school/tasks/${taskId}/attachments/${id}/`).then(() => undefined),

  uploadExamAttachments: (examId: number, files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return API.post<{ attachments: Attachment[] }>(`/api/school/exams/${examId}/attachments/`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  deleteExamAttachment: (examId: number, id: number) =>
    API.delete(`/api/school/exams/${examId}/attachments/${id}/`).then(() => undefined),

  downloadAttachment: async (a: Attachment) => {
    // Open the tab synchronously (inside the click's user activation) so the
    // browser does not block it after the network round trip.
    const viewable = a.mime === 'application/pdf' || a.mime.startsWith('image/');
    const w = viewable ? window.open('', '_blank') : null;
    let blob: Blob;
    try {
      blob = (await API.get<Blob>(a.url, { responseType: 'blob' })).data;
    } catch (e) {
      w?.close();
      throw e;
    }
    if (w) {
      const url = URL.createObjectURL(new Blob([blob], { type: a.mime }));
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      saveBlob(blob, a.name);
    }
  },

  sheetsList: (examId: number, status?: string) =>
    API.get<SheetsListResponse>(`/api/school/exams/${examId}/sheets/`,
      { params: status ? { status } : {} }).then(r => r.data),

  sheetsUpload: (examId: number, files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return API.post<SheetsUploadResponse>(`/api/school/exams/${examId}/sheets/`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  sheetsDetail: (examId: number, scanId: number) =>
    API.get<SheetDetail>(`/api/school/exams/${examId}/sheets/${scanId}/`).then(r => r.data),

  sheetsPatch: (examId: number, scanId: number, body: SheetPatchPayload) =>
    API.patch<SheetDetail>(`/api/school/exams/${examId}/sheets/${scanId}/`, body).then(r => r.data),

  sheetsConfirm: (examId: number, scanId: number, replace = false) =>
    API.post<SheetDetail>(`/api/school/exams/${examId}/sheets/${scanId}/confirm/`, { replace }).then(r => r.data),

  sheetsConfirmMatched: (examId: number) =>
    API.post<SheetsConfirmMatchedResponse>(`/api/school/exams/${examId}/sheets/confirm-matched/`, {}).then(r => r.data),

  /** Re-read a sheet; `corners` (four [x, y] fractions of the photo, TL TR BR BL) are the
   * corner marks the teacher clicked when the reader could not find them. */
  /** Mark the exam's written questions by hand, or leave them out of every sheet's score. */
  sheetsWritten: (examId: number, mode: PaperWritten) =>
    API.post<{ paper_written: PaperWritten; regraded: number }>(`/api/school/exams/${examId}/sheets/written/`, { mode }).then(r => r.data),

  sheetsReread: (examId: number, scanId: number, corners?: [number, number][] | null) =>
    API.post<SheetDetail>(`/api/school/exams/${examId}/sheets/${scanId}/reread/`,
      corners !== undefined ? { corners } : {}).then(r => r.data),

  authoringExam: (examId: number) =>
    API.get<AuthoringExam>(`/api/school/exams/${examId}/`).then(r => r.data),

  meTeaching: () => API.get<MeTeaching>('/api/school/me/teaching/').then(r => r.data),

  // Exam Bank: released school exams any staff member may read and copy.
  examBank: (params: ExamBankParams) =>
    API.get<ExamBankListResponse>('/api/school/exam-bank/', { params }).then(r => r.data),

  examBankDetail: (examId: number) =>
    API.get<ExamBankDetail>(`/api/school/exam-bank/${examId}/`).then(r => r.data),

  examBankCopy: (examId: number, body: { grade_id: number; subject_id: number }) =>
    API.post<{ id: number; kind: string; name: string }>(`/api/school/exam-bank/${examId}/copy/`, body).then(r => r.data),

  authoringQuestionCreate: (examId: number, body: QuestionPayload) =>
    API.post<AuthoringQuestion>(`/api/school/exams/${examId}/questions/`, body).then(r => r.data),

  authoringQuestionUpdate: (examId: number, qid: number, body: QuestionPayload) =>
    API.patch<AuthoringQuestion>(`/api/school/exams/${examId}/questions/${qid}/`, body).then(r => r.data),

  /** AI reads questions (with their types) from PDFs, Word files or photos; nothing is saved. */
  authoringImport: (examId: number, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return API.post<{ questions: ImportedQuestion[] }>(`/api/school/exams/${examId}/import-questions/`, fd, { timeout: 180_000 })
      .then(r => r.data.questions);
  },

  authoringAnnotateBooks: (examId: number) =>
    API.get<{ books: AnnotateBook[] }>(`/api/school/exams/${examId}/annotate-books/`).then(r => r.data.books),

  /** Chapter, explanation and reference for up to 10 imported questions, from the chosen books. */
  authoringAnnotate: (examId: number, questions: QuestionPayload[], bookIds: number[]) =>
    API.post<{ results: AnnotateResult[] }>(`/api/school/exams/${examId}/annotate-questions/`,
      { questions, book_ids: bookIds }, { timeout: 180_000 }).then(r => r.data.results),

  /** Every grade's chapters of the worksheet's subject, with their grades. */
  authoringChapters: (examId: number) =>
    API.get<AuthoringChapter[]>(`/api/school/exams/${examId}/chapters/`, { params: { all_grades: 1 } }).then(r => r.data),

  authoringQuestionDelete: (examId: number, qid: number) =>
    API.delete(`/api/school/exams/${examId}/questions/${qid}/`).then(() => undefined),

  authoringPublish: (examId: number) =>
    API.post<AuthoringExam>(`/api/school/exams/${examId}/publish/`).then(r => r.data),

  markingSummary: () =>
    API.get<MarkingSummary>('/api/school/marking/summary/').then(r => r.data),

  examMarking: (examId: number) =>
    API.get<ExamMarking>(`/api/school/exams/${examId}/marking/`).then(r => r.data),

  examMarkingQuestion: (examId: number, qid: number, status: MarkingStatus) =>
    API.get<MarkingQuestion>(`/api/school/exams/${examId}/marking/${qid}/`, { params: { status } }).then(r => r.data),

  confirmWrittenMark: (id: number, marks: number) =>
    API.put<ConfirmMarkResult>(`/api/school/written-marks/${id}/`, { marks }).then(r => r.data),
};
